<template>
  <component
    :is="layout"
    :style-override="styleOverride"
  >
    <router-view />
    <v-snackbar-queue />
  </component>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import i18n from "./plugins/i18n";
import { getOrgDisplayName } from "@/utils/org";
import { setDefaultSearchCriteriaDates } from "@/utils/searchCriteria";
import { useOidc } from '@/composables/oidc';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const DEFAULT_LAYOUT = "app";

export default {
  name: "App",
  setup() {
    const oidc = useOidc();
    const user = computed(() => oidc.user);
    const route = useRoute();
    return { user, route };
  },
  methods: {
    userLoaded: async function (user) {
      await this.fetchFunctionTypes();
      await this.fetchFunctions({
        personId: user.personid
      });
      await this.fetchCurrentOrgs({
        personId: user.personid
      });
      await this.fetchInfoMessages();
      await this.initContext(this.orgsWithPermissions.map(x => x.org));
      setDefaultSearchCriteriaDates(user.personid);
    },
    ...mapActions({
      fetchFunctionTypes: "functionTypes/fetch",
      initContext: "contextStore/init",
      fetchFunctions: "currentOrgsStore/fetchFunctions",
      fetchCurrentOrgs: "currentOrgsStore/fetch",
      fetchInfoMessages: "infoMessagesStore/fetch"
    })
  },
  computed: {
    layout() {
      return `${this.route.meta.layout || DEFAULT_LAYOUT}-layout`;
    },
    styleOverride() {
      return this.$route.meta.styleOverride;
    },
    ...mapGetters({
      currentOrgId: "contextStore/orgId",
      orgsWithPermissions: "currentOrgsStore/orgs",
      messages: "infoMessagesStore/messages"
    }),
    currentOrgName() {
      const currentOrg = this.orgsWithPermissions.find(
        x => x.org.id === this.currentOrgId
      );
      if (currentOrg) {
        return getOrgDisplayName(currentOrg?.org);
      }

      return "";
    }
  },
  watch: {
    user(newValue) {
      if (newValue)
        this.userLoaded(newValue);
    },
    currentOrgId(_newValue, oldValue) {
      if (
        oldValue &&
        this.orgsWithPermissions &&
        this.orgsWithPermissions.some(x => x.org.id === oldValue)
      ) {
        window.location.href = "/orgapplications";
      }
    },
    route(to) {
      const values = [to.meta.title];

      if (to.meta.addTitleId && to.params.id) {
        values.push(`${to.params.id}`);
      }

      if (to.meta.addTitleOrgName) {
        values.push(this.currentOrgName);
      } else {
        values.push(i18n.global.t("commonStrings.appName"));
      }

      document.title = values.filter(Boolean).join(" - ");
    }
  }
};
</script>

<style lang="scss">
  @use './plugins/vuetify/scss/main.scss';
</style>
